import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface OnlineUser {
  user_id: string;
  full_name: string;
  system_role: string;
  last_seen_at: string;
}

export function useUserPresence() {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Mettre à jour la présence de l'utilisateur courant
  const updatePresence = useCallback(async () => {
    if (!user) return;

    try {
      // Essayer d'abord via RPC (si la fonction existe)
      const rpc = supabase.rpc as any;
      const { error } = await rpc('update_user_presence');
      
      if (error) {
        // Fallback: mise à jour directe si la colonne existe
        const client = supabase as any;
        await client
          .from('profiles')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('id', user.id);
      }
    } catch {
      // Silencieux - la colonne/fonction n'existe peut-être pas encore
    }
  }, [user]);

  // Récupérer la liste des utilisateurs en ligne
  const fetchOnlineUsers = useCallback(async () => {
    try {
      // Appeler la RPC get_online_users
      const { data, error } = await supabase.rpc('get_online_users');
      
      if (!error && data && Array.isArray(data)) {
        // Le SQL retourne 'user_id' (alias de p.id)
        const users: OnlineUser[] = data.map((u: any) => ({
          user_id: u.user_id || u.id,
          full_name: u.full_name || 'Utilisateur',
          system_role: u.system_role || 'user',
          last_seen_at: u.last_seen_at || ''
        }));
        setOnlineUsers(users);
        setOnlineUserIds(new Set(users.map(u => u.user_id)));
        return;
      }
      
      // Fallback: utiliser admin_sessions pour les admins (système existant)
      const { data: adminData } = await supabase.rpc('get_online_admins');
      
      if (adminData && Array.isArray(adminData)) {
        const users: OnlineUser[] = adminData.map((u: any) => ({
          user_id: u.user_id,
          full_name: u.full_name || 'Admin',
          system_role: u.system_role || 'admin',
          last_seen_at: u.last_activity || ''
        }));
        setOnlineUsers(users);
        setOnlineUserIds(new Set(users.map(u => u.user_id)));
      }
    } catch {
      // Fallback silencieux si erreur
      try {
        const { data: adminData } = await supabase.rpc('get_online_admins');
        if (adminData && Array.isArray(adminData)) {
          const users: OnlineUser[] = adminData.map((u: any) => ({
            user_id: u.user_id,
            full_name: u.full_name || 'Admin',
            system_role: u.system_role || 'admin',
            last_seen_at: u.last_activity || ''
          }));
          setOnlineUsers(users);
          setOnlineUserIds(new Set(users.map(u => u.user_id)));
        }
      } catch {
        // Aucun système de présence disponible
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Vérifier si un utilisateur spécifique est en ligne
  const isOnline = useCallback((userId: string): boolean => {
    return onlineUserIds.has(userId);
  }, [onlineUserIds]);

  // Effet pour le heartbeat de présence
  useEffect(() => {
    if (!user) return;

    // Mise à jour immédiate
    updatePresence();
    fetchOnlineUsers();

    // Heartbeat toutes les 30 secondes
    const heartbeatInterval = setInterval(updatePresence, 30000);
    
    // Rafraîchir la liste toutes les 30 secondes
    const fetchInterval = setInterval(fetchOnlineUsers, 30000);

    // Nettoyage à la déconnexion
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(fetchInterval);
    };
  }, [user, updatePresence, fetchOnlineUsers]);

  // Abonnement temps réel aux changements de profils (optionnel)
  useEffect(() => {
    const channel = supabase
      .channel('online-users-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          // Rafraîchir la liste quand un profil est mis à jour
          fetchOnlineUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchOnlineUsers]);

  return {
    onlineUsers,
    isOnline,
    loading,
    refreshOnlineUsers: fetchOnlineUsers
  };
}
